/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import { renderErrorMsg } from "../../../assets/component/renderErrorMsg.js";
import RenderPaymentCode from "./RenderPaymentCode";
import ToggleSwitch from "../../../assets/component/ToggleSwitch/ToggleSwitch.js";
import { mapPaymentMethodDataStatus, mapPaymentMethodData } from "../../../assets/helper.js";
import PropTypes from "prop-types";
import UpgradeNoticeCard from "./UpgradeNoticeCard";

const paymentMethods = [
  {
    key: "fps",
    label: "FPS",
  }
];

const defaultFormData = {
  fpsIdentifier: '',
  fpsEmailAddress: '',
  fpsPhoneNumber: '',
  fpsBankCode: '',
  fpsBankBranchCode: '',
  fpsBankAccountName: '',
  fpsBankAccountNumber: '',
};

class RenderFPS extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isEditMode: false,
      formData: defaultFormData,
      isOtherBank: false,
    };

    this.handleToggleEditMode = this.handleToggleEditMode.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleBankSelectChange = this.handleBankSelectChange.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.mapFormDataFromProps = this.mapFormDataFromProps.bind(this);
    this.mapFieldText = this.mapFieldText.bind(this);
    this.renderErrorMsg = this.renderErrorMsg.bind(this);
  }

  componentDidMount() {
    const newFormData = this.mapFormDataFromProps();

    // Check if the saved bank code is in the list, if not set isOtherBank
    const bankList = this.getBankList();
    const savedBankCode = newFormData.fpsBankCode;
    const isInList = bankList.some(bank => bank[1] === savedBankCode);
    const isOtherBank = savedBankCode && !isInList;

    this.setState({
      formData: newFormData,
      isOtherBank,
    });
  }

  handleToggleEditMode() {
    const { isEditMode } = this.state;

    const newState = !isEditMode;

    if (newState === false) {
      // reset form data when cancel edit
      const newFormData = this.mapFormDataFromProps();
      this.setState({
        formData: newFormData,
      });
    }

    this.setState({
      isEditMode: newState,
    });
  }

  handleInputChange(e) {
    const { name, value } = e.target;
    this.setState((prevState) => ({
      formData: {
        ...prevState.formData,
        [name]: value,
      },
    }));
  }

  handleBankSelectChange(e) {
    const { value } = e.target;

    if (value === "OTHER") {
      // User selected "Other" - show text input
      this.setState((prevState) => ({
        isOtherBank: true,
        formData: {
          ...prevState.formData,
          fpsBankCode: '',
        },
      }));
    } else {
      // User selected a bank from the list
      this.setState((prevState) => ({
        isOtherBank: false,
        formData: {
          ...prevState.formData,
          fpsBankCode: value,
        },
      }));
    }
  }

  handleSave() {
    const { formData } = this.state;
    const { func } = this.props;

    func.handleUpdatePaymentData(paymentMethods[0].key, paymentMethods[0].label, formData);

    this.setState({ isEditMode: false });
  }

  mapFormDataFromProps() {
    const { state } = this.props;
    const { formData } = this.state;

    let newFormData = formData;

    const paymentCodes = state.organizationObj.paymentMethodCode;

    if (paymentCodes) {
      const getPaymentData = paymentCodes[paymentMethods[0].key] || null;

      if (getPaymentData) {
        // reset to latest data from props
        newFormData = {
          fpsIdentifier: getPaymentData.fpsIdentifier || '',
          fpsEmailAddress: getPaymentData.fpsEmailAddress || '',
          fpsPhoneNumber: getPaymentData.fpsPhoneNumber || '',
          fpsBankCode: getPaymentData.fpsBankCode || '',
          fpsBankBranchCode: getPaymentData.fpsBankBranchCode || '',
          fpsBankAccountName: getPaymentData.fpsBankAccountName || '',
          fpsBankAccountNumber: getPaymentData.fpsBankAccountNumber || '',
        };
      } else {
        // reset to default
        newFormData = defaultFormData;
      }
    }

    return newFormData;
  }

  mapFieldText(field)  {
    const { formData } = this.state;

    let text = '-';

    if (formData[field] !== '') {
      text = formData[field];
    }

    return text;
  }

  getBankList() {
    const { languageObj } = this.props;
    // Get bank list from languageObj, fallback to default bank list
    const bankList = languageObj?.OrgBankAdd?.bankList;

    if (bankList && bankList.length > 0) {
      return bankList;
    }

    // Fallback bank list for Hong Kong banks
    return [
      ["Select a Bank", ""],
      ["HSBC Bank (004)", "004"],
      ["Hang Seng Bank Limited (024)", "024"],
      ["Standard Chartered Bank (003)", "003"],
      ["Bank of China (Hong Kong) (012)", "012"],
      ["Bank of East Asia (015)", "015"],
      ["DBS Bank (Hong Kong) (016)", "016"],
      ["Citibank (N.A.) (006)", "006"],
      ["China Construction Bank (009)", "009"],
      ["Industrial and Commercial Bank of China (072)", "072"],
      ["Nanyang Commercial Bank (043)", "043"],
      ["OCBC Wing Hang Bank (035)", "035"],
      ["Shanghai Commercial Bank (025)", "025"],
      ["Public Bank (Hong Kong) (028)", "028"],
      ["Chiyu Banking Corporation (039)", "039"],
      ["Dah Sing Bank (040)", "040"],
      ["Fubon Bank (Hong Kong) (128)", "128"],
      ["China CITIC Bank International (018)", "018"],
      ["CMB Wing Lung Bank (020)", "020"],
      ["Chong Hing Bank (041)", "041"],
      ["Other (Enter Bank Code)", "OTHER"]
    ];
  }

  getBankNameFromCode(code) {
    if (!code) {
      return '-';
    }
    const bankList = this.getBankList();
    const bank = bankList.find(b => b[1] === code);
    return bank ? bank[0] : code;
  }

  renderErrorMsg() {
    const { state } = this.props;
    const errMsg = state.errMsgInfo.paymentCode;

    if (errMsg.method === paymentMethods[0].key && !errMsg.isCode) {
      return renderErrorMsg(errMsg.msg);
    }

    return null;
  }

  render() {
    const { state, func } = this.props;
    const { isEditMode, formData, isOtherBank } = this.state;

    const fpsStatus = mapPaymentMethodDataStatus(state.organizationObj.paymentMethodCode, paymentMethods[0].key);
    const fpsData = mapPaymentMethodData(state.organizationObj.paymentMethodCode, paymentMethods[0].key);
    // FPS is configured if at least one required field has data
    const fpsIsConfigured = Boolean(
      fpsData.fpsIdentifier ||
      fpsData.fpsEmailAddress ||
      fpsData.fpsPhoneNumber ||
      fpsData.fpsBankAccountNumber
    );

    if (state.loading) {
      return (
        <div className="RenderFPS-loading">
          <div className="RenderFPS-spinner"></div>
        </div>
      );
    }

    const currentPlan = state.organizationObj.currentPlan
      ? state.organizationObj.currentPlan.toLowerCase()
      : "";
    const isPremiumPlan = currentPlan === "premium";

    if (!isPremiumPlan) {
      return (
        <div className="RenderFPS-container">
          <UpgradeNoticeCard onUpgradeClick={func.redirectToOrgBilling} />
        </div>
      );
    }

    return (
      <div className="RenderFPS-container">
        <div className="RenderFPS-content">
          <div className="RenderFPS-toggleRow">
            <span className="RenderFPS-toggleLabel">Enable FPS</span>
            <ToggleSwitch
              checked={fpsStatus.checked}
              onChange={(e) => func.handleUpdatePaymentStatus(e, paymentMethods[0].key)}
              disabled={!fpsIsConfigured}
              disabledMessage={!fpsIsConfigured ? "Please fill in the required fields first" : ""}
            />
          </div>

          <div className="RenderFPS-headerSection">
            <div className="RenderFPS-fpsLogo" />
            <div className="RenderFPS-qrCodeContainer">
              <RenderPaymentCode state={state} func={func} paymentMethod={paymentMethods[0]} />
            </div>
          </div>

          <div className="RenderFPS-fpsGroup">

            <div className="RenderFPS-savedCredentials">
              <div className="RenderFPS-group">
                <div className="RenderFPS-subheading">FPS Identifier</div>
                {isEditMode ? (
                  <div className="RenderFPS-inputFieldContainer">
                    <input
                      type="text"
                      name="fpsIdentifier"
                      className="RenderFPS-inputField"
                      placeholder="FPS Identifier"
                      value={formData.fpsIdentifier}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderFPS-textValue">
                    {this.mapFieldText('fpsIdentifier')}
                  </div>
                )}
              </div>
             
              <div className="RenderFPS-group">
                <div className="RenderFPS-subheading">FPS - Email Address</div>
                {isEditMode ? (
                  <div className="RenderFPS-inputFieldContainer">
                    <input
                      type="text"
                      name="fpsEmailAddress"
                      className="RenderFPS-inputField"
                      placeholder="FPS - Email Address"
                      value={formData.fpsEmailAddress}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderFPS-textValue">
                    {this.mapFieldText('fpsEmailAddress')}
                  </div>
                )}
              </div>

             
              <div className="RenderFPS-group">
                <div className="RenderFPS-subheading">FPS - Phone Number</div>
                {isEditMode ? (
                  <div className="RenderFPS-inputFieldContainer">
                    <input
                      type="text"
                      name="fpsPhoneNumber"
                      className="RenderFPS-inputField"
                      placeholder="FPS - Phone Number"
                      value={formData.fpsPhoneNumber}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderFPS-textValue">
                    {this.mapFieldText('fpsPhoneNumber')}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="RenderFPS-bankGroup">
            <div className="RenderFPS-bankLogo" />

            <div className="RenderFPS-savedCredentials">
              <div className="RenderFPS-group">
                <div className="RenderFPS-subheading">Bank</div>
                {isEditMode ? (
                  <div className="RenderFPS-inputFieldContainer">
                    <select
                      name="fpsBankCode"
                      className="RenderFPS-inputField"
                      value={isOtherBank ? "OTHER" : formData.fpsBankCode}
                      onChange={this.handleBankSelectChange}
                    >
                      {this.getBankList().map((bank) => (
                        <option key={bank[1]} value={bank[1]}>
                          {bank[0]}
                        </option>
                      ))}
                    </select>
                    {isOtherBank && (
                      <input
                        type="text"
                        name="fpsBankCode"
                        className="RenderFPS-inputField"
                        placeholder="Enter Bank Code (e.g., 004)"
                        value={formData.fpsBankCode}
                        onChange={this.handleInputChange}
                        style={{ marginTop: '10px' }}
                      />
                    )}
                  </div>
                ) : (
                  <div className="RenderFPS-textValue">
                    {this.getBankNameFromCode(formData.fpsBankCode)}
                  </div>
                )}
              </div>

              <div className="RenderFPS-group">
                <div className="RenderFPS-subheading">Branch Code</div>
                {isEditMode ? (
                  <div className="RenderFPS-inputFieldContainer">
                    <input
                      type="text"
                      name="fpsBankBranchCode"
                      className="RenderFPS-inputField"
                      placeholder="Branch Code"
                      value={formData.fpsBankBranchCode}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderFPS-textValue">
                    {this.mapFieldText('fpsBankBranchCode')}
                  </div>
                )}
              </div>

              <div className="RenderFPS-group">
                <div className="RenderFPS-subheading">Account Name</div>
                {isEditMode ? (
                  <div className="RenderFPS-inputFieldContainer">
                    <input
                      type="text"
                      name="fpsBankAccountName"
                      className="RenderFPS-inputField"
                      placeholder="Account Name"
                      value={formData.fpsBankAccountName}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderFPS-textValue">
                    {this.mapFieldText('fpsBankAccountName')}
                  </div>
                )}
              </div>

              <div className="RenderFPS-group">
                <div className="RenderFPS-subheading">Account Number</div>
                {isEditMode ? (
                  <div className="RenderFPS-inputFieldContainer">
                    <input
                      type="text"
                      name="fpsBankAccountNumber"
                      className="RenderFPS-inputField"
                      placeholder="Account Number"
                      value={formData.fpsBankAccountNumber}
                      onChange={this.handleInputChange}
                    />
                  </div>
                ) : (
                  <div className="RenderFPS-textValue">
                    {this.mapFieldText('fpsBankAccountNumber')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {this.renderErrorMsg()}
        </div>
      </div>
    );
  }
}

RenderFPS.propTypes = {
  state: PropTypes.object.isRequired,
  func: PropTypes.object.isRequired,
  languageObj: PropTypes.object,
};

export default RenderFPS;
